import { TranslateService } from '@ngx-translate/core';
import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';
import { IntlService, CldrIntlService } from '@progress/kendo-angular-intl';
import moment from 'moment';
import '@progress/kendo-angular-intl/locales/en/all';
import '@progress/kendo-angular-intl/locales/en-GB/all';
import '@progress/kendo-angular-intl/locales/en-CA/all';
import '@progress/kendo-angular-intl/locales/es/all';
import '@progress/kendo-angular-intl/locales/es-US/all';
import '@progress/kendo-angular-intl/locales/de/all';
import { FormlyValidationLoader } from 'src/app/formly/formly-validation-loader';
import { NGXLogger } from 'ngx-logger';
import { KendoMessageService } from './kendo-message.service';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class LanguageService
{
  // None primary languages are filtered out except from login
  public languages: Array<{ text: string; value: string; primary: boolean }> = [
    { text: 'English (United States)', value: 'en', primary: true },
    { text: 'English (United Kingdom)', value: 'en-GB', primary: false },
    { text: 'English (Canada)', value: 'en-CA', primary: false },
    { text: 'Español (Spain)', value: 'es', primary: true },
    { text: 'Español (United States)', value: 'es-US', primary: false },
    { text: 'Deutsch', value: 'de', primary: true }
  ];

  constructor(
    private localStorageService: LocalStorageService,
    private translate: TranslateService,
    private formlyValidationsLoader: FormlyValidationLoader,
    @Inject(LOCALE_ID) public localeId: string,
    private logger: NGXLogger,
    private kendoMessageService: KendoMessageService,
    private http: HttpClient,
    private intlService: IntlService) { }

  public get currentLanguage(): string
  {
    const defaultLanguage = this.localStorageService.retrieve('default-language') || 'en';
    if (this.languages.filter(x => x.value === defaultLanguage).length > 0)
    {
      return defaultLanguage;
    }
    else
    {
      return 'en';
    }
  }

  public get currentCountry(): string
  {
    return this.findCountry(this.currentLanguage);
  }

  public setLanguage(newLanguage: string): void
  {
    this.localStorageService.store('default-language', newLanguage);
  }

  public switchLanguage(language: string): void
  {
    // Set i18n translations
    this.translate.use(language.includes('-') ? language.substring(0, 2) : language);
    // For now we will use en.json but if adding es-GB.json one day, line above show be replaced by line below.
    // this.translate.use(language);

    // Set moment locale
    moment.locale(language);

    // Set formly translations
    this.formlyValidationsLoader.loadLocalizations();

    // Set kendo locale
    (this.intlService as CldrIntlService).localeId = language;

    // Set kendo messages language for components translations
    this.kendoMessageService.language = language;

    this.logger.info(`Set language = ${language}, country = ${this.findCountry(language)}`);
  }

  /**
   * Convert a list of login languages to texts.
   *
   * @param loginLanguages List of login languages.
   */
  public convertLoginLanguagesToDisplayTexts(loginLanguages: string): string
  {
    const res = loginLanguages.split(',');
    const languagesText = [];
    res.forEach(language =>
    {
      const languageText = this.getLoginLanguageText(language);
      if (languageText !== '')
      {
        languagesText.push(languageText);
      }
    });
    return languagesText.join();
  }

  /**
   * Get the Login Language text to display.
   *
   * @param language Login Language string.
   */
  public getLoginLanguageText(language: string): string
  {
    let text = language;
    const languageObject = this.languages.find(x => x.value === language);
    if (language !== undefined)
    {
      text = languageObject.text;
    }
    return text;
  }

  public getLanguageAssets(assetsName: string): Observable<any>
  {
    return this.http.get<any>(`assets/i18n/${assetsName}`);
  }

  /**
   * Determind the country based on the default language
   *
   * @param language en, en-GB, es or de.
   * @returns Country code supported by libphonenumber-js
   *             en returns US
   *             en-GB returns GB
   *             en-CA returns CA
   *             es returne ES
   *             de returns DE
   */
  private findCountry(language: string): string
  {
    return language.includes('-') ? language.split('-')[1] : (language === 'en' ? 'US' : language.toUpperCase());
  }
}
